import { useMemo } from 'react';
import { PricingPlan, LimitPeriod } from '@cyberlabrs/helpers';
import { useAppSelector } from '../../store';
import { selectFeatureConfig } from '../../store/slices/feature-config.slice';
import { selectActiveSubscription } from '../../store/slices/active-subscription.slice';
import { useFeatureAvailability } from './useFeatureAvailability';
import { ActiveSubscriptionResponseDto, FeaturePricingPlanName } from '../../commonTypes/organization.types';
import { useFeatureFlag } from '../featureFlags/useFeatureFlags';
import { AppConfig } from '../../config/AppConfig';

export type CanCreateResult = {
    canCreate: boolean;
    reason: string | null;
    limit: number | null;
    currentCount: number | null;
    period: LimitPeriod | null;
};

export const useCanCreate = (featureName: string, action: string, currentCount?: number | null): CanCreateResult => {
    const featureAvailability = useFeatureAvailability(featureName);
    const configs = useAppSelector(selectFeatureConfig);
    const activeSubscription = useAppSelector(selectActiveSubscription);
    const billingEnabled = useFeatureFlag('BILLING');
    const isDevelopment = AppConfig.isDevelopment;

    return useMemo(() => {
        // If billing is disabled in production, bypass all checks and allow creation
        // In development, always run checks so we can test the tooltip behavior
        if (!billingEnabled && !isDevelopment) {
            return {
                canCreate: true,
                reason: null,
                limit: null,
                currentCount: currentCount !== undefined ? currentCount : null,
                period: null,
            };
        }
        if (!featureAvailability.isAvailable) {
            return {
                canCreate: false,
                reason: featureAvailability.reason || 'FEATURE_DISABLED',
                limit: null,
                currentCount: null,
                period: null,
            };
        }

        const cfg = configs.find(c => c.name === featureName);
        if (!cfg) {
            return {
                canCreate: false,
                reason: 'NO_CONFIG',
                limit: null,
                currentCount: null,
                period: null,
            };
        }

        const subscription = activeSubscription as ActiveSubscriptionResponseDto | null;
        const rawPlanName = subscription?.name?.toUpperCase() as FeaturePricingPlanName | undefined;
        const PLAN_ORDER: FeaturePricingPlanName[] = [PricingPlan.FREE, PricingPlan.PREMIUM, PricingPlan.ENTERPRISE, PricingPlan.LIFETIME];
        const currentPlan: FeaturePricingPlanName = rawPlanName && PLAN_ORDER.includes(rawPlanName) ? rawPlanName : PricingPlan.FREE;

        if (currentPlan === PricingPlan.LIFETIME) {
            return {
                canCreate: true,
                reason: null,
                limit: null,
                currentCount: null,
                period: null,
            };
        }

        const planConfig = cfg.config.subscription[currentPlan];

        const limitation = planConfig?.limitations?.[action] as
            | {
                  enabled?: boolean;
                  limit?: {
                      value: number | boolean | null;
                      period: LimitPeriod;
                  };
              }
            | undefined;

        if (!limitation) {
            return {
                canCreate: true,
                reason: null,
                limit: null,
                currentCount: null,
                period: null,
            };
        }

        // Check if limitation is disabled
        if (limitation.enabled === false) {
            return {
                canCreate: false,
                reason: 'LIMITATION_DISABLED',
                limit: typeof limitation.limit?.value === 'number' ? limitation.limit.value : null,
                currentCount: null,
                period: limitation.limit?.period || null,
            };
        }

        const limitValue = limitation.limit?.value;
        const limit = typeof limitValue === 'number' ? limitValue : null;

        const count = currentCount !== undefined ? currentCount : null;

        const isLimitReached = limit !== null && limit !== undefined && count !== null && count >= limit;

        return {
            canCreate: !isLimitReached,
            reason: isLimitReached ? 'LIMIT_REACHED' : null,
            limit,
            currentCount: count,
            period: limitation.limit?.period || null,
        };
    }, [featureAvailability, configs, activeSubscription, featureName, action, currentCount, billingEnabled, isDevelopment]);
};
